import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import { getVendorContractEditHistoryV2 } from 'store/vendorSliceV2';
import { getClientContractEditHistoryV2 } from 'store/clientSliceV2';

const SectionContainer = styled(Box)`
	margin-top: 32px;
`;

const SectionTitle = styled(Text)`
	font-size: 18px;
	font-weight: 600;
	color: #1c1c1c;
	display: block;
	margin-bottom: 12px;
`;

const titleCase = (key = '') =>
	key
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.trim();

const isIsoDateString = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(val);

const MONEY_FIELDS = new Set([
	'vehicle_cost',
	'loader_cost',
	'delivery_associate_cost',
	'extra_charges_per_km',
	'extra_charges_per_hour',
	'extra_charges_per_trip',
	'charges_per_km',
	'other_expenses_value',
]);

const isMoneyField = (field) => MONEY_FIELDS.has(field);

// Document keys are stored as "{uuid}-{uuid}-{original filename}"; strip every
// leading UUID prefix so only the human-readable filename remains.
const UUID_PREFIX = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;

const stripUuidPrefixes = (key) => {
	let result = String(key);
	while (UUID_PREFIX.test(result)) {
		result = result.replace(UUID_PREFIX, '');
	}
	return result;
};

// Document values can be nested (e.g. { signed_contract_file: { document_key } });
// dig out the first document_key found at any depth.
const findDocumentKey = (obj) => {
	if (!obj || typeof obj !== 'object') return null;
	if (typeof obj.document_key === 'string') return obj.document_key;
	return Object.values(obj).reduce((found, v) => found || findDocumentKey(v), null);
};

const formatValue = (val, field) => {
	if (val === null || val === undefined || val === '') return '-';
	if (typeof val === 'boolean') return val ? 'Yes' : 'No';
	if (isIsoDateString(val)) {
		const m = moment(val);
		if (m.isValid()) return m.format('DD-MM-YYYY');
	}
	if (typeof val === 'object') {
		const docKey = findDocumentKey(val);
		if (docKey) return stripUuidPrefixes(docKey);
		try {
			return JSON.stringify(val);
		} catch (e) {
			return String(val);
		}
	}
	if (isMoneyField(field) && val !== '' && Number.isFinite(Number(val))) {
		return `₹${val}`;
	}
	return String(val);
};
// Per-order rate lines render as a stacked breakdown, e.g. "R1: ₹11" per line.
// Items without a rate_id (newly added lines) fall back to a positional label.
const renderOrderRatesCell = (val) => {
	if (!Array.isArray(val) || val.length === 0) return <Text>-</Text>;
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{val.map((line, i) => (
				// eslint-disable-next-line react/no-array-index-key
				<Text key={line?.rate_id || `line-${i}`}>
					{line?.rate_id || `Line ${i + 1}`}: ₹{line?.rate}
				</Text>
			))}
		</div>
	);
};

// Expand server-side audit rows (one row per submission with a `field_diff` or
// `changes` blob) into one row per field-change.
const expandRows = (rows) => {
	if (!Array.isArray(rows)) return [];
	const out = [];
	rows.forEach((row, rowIdx) => {
		const fieldDiff = row?.field_diff || {};
		const changes = row?.changes || {};
		const initialValues = row?.initial_values || row?.previous_values || {};
		const updatedBy = row?.requested_by_name || row?.updated_by_name || row?.updated_by || '-';
		const updatedAt =
			row?.requested_at || row?.resolved_at || row?.updated_at || row?.created_at || null;
		const baseId = row?.id ?? row?.approval_id ?? rowIdx;

		const fieldDiffKeys = Object.keys(fieldDiff);
		if (fieldDiffKeys.length > 0) {
			fieldDiffKeys.forEach((field) => {
				const entry = fieldDiff[field];
				out.push({
					key: `${baseId}-${field}`,
					field,
					initialValue: entry?.old,
					updatedValue: entry?.new,
					updatedBy,
					updatedAt,
				});
			});
			return;
		}

		const changeKeys = Object.keys(changes);
		if (changeKeys.length > 0) {
			changeKeys.forEach((field) => {
				out.push({
					key: `${baseId}-${field}`,
					field,
					initialValue: initialValues?.[field],
					updatedValue: changes[field],
					updatedBy,
					updatedAt,
				});
			});
			return;
		}

		if (row?.field !== undefined) {
			out.push({
				key: `${baseId}-${row.field}`,
				field: row.field,
				initialValue: row.initial_value ?? row.previous_value ?? row.old_value,
				updatedValue: row.updated_value ?? row.new_value,
				updatedBy,
				updatedAt,
			});
		}
	});
	return out;
};

const buildColumns = () => [
	{
		title: 'Field',
		dataIndex: 'field',
		key: 'field',
		render: (text) => <Text>{titleCase(text)}</Text>,
	},
	{
		title: 'Initial Value',
		dataIndex: 'initialValue',
		key: 'initialValue',
		render: (val, record) =>
			record?.field === 'order_rates' ? (
				renderOrderRatesCell(val)
			) : (
				<Text>{formatValue(val, record?.field)}</Text>
			),
	},
	{
		title: 'Updated Value',
		dataIndex: 'updatedValue',
		key: 'updatedValue',
		render: (val, record) =>
			record?.field === 'order_rates' ? (
				renderOrderRatesCell(val)
			) : (
				<Text>{formatValue(val, record?.field)}</Text>
			),
	},
	{
		title: 'Updated By',
		dataIndex: 'updatedBy',
		key: 'updatedBy',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Updated At',
		dataIndex: 'updatedAt',
		key: 'updatedAt',
		render: (val) => {
			if (!val) return <Text>-</Text>;
			const m = moment(val).add(5, 'hours').add(30, 'minutes');
			return <Text>{m.isValid() ? m.format('DD-MM-YYYY HH:mm') : '-'}</Text>;
		},
	},
];

export default function ContractEditHistoryTable({ contractId, contractType, status }) {
	const dispatch = useDispatch();
	const [rawData, setRawData] = useState({ rows: [], count: 0 });
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [perPage, setPerPage] = useState(10);

	const columns = useMemo(() => buildColumns(), []);

	const baseThunk =
		contractType === 'client' ? getClientContractEditHistoryV2 : getVendorContractEditHistoryV2;

	// Server pagination is keyed on audit submissions, but each submission expands
	// into multiple field-change rows for display, so the two can't be paginated
	// server-side together. Fetch the full history once and paginate the expanded
	// rows entirely client-side.
	const fetchHistory = useCallback(() => {
		if (!contractId) return;
		setLoading(true);
		dispatch(
			baseThunk({
				contract_id: contractId,
				status,
				pagination: { page: 1, per_page: 1000 },
			})
		)
			.then((res) => setRawData(res || { rows: [], count: 0 }))
			.finally(() => setLoading(false));
	}, [dispatch, baseThunk, contractId, status]);

	useEffect(() => {
		setPage(1);
		fetchHistory();
	}, [fetchHistory]);

	const expandedRows = useMemo(() => expandRows(rawData?.rows || []), [rawData]);

	const handleChange = (pag) => {
		const nextPage = pag?.current || 1;
		const nextPerPage = pag?.pageSize || 10;
		if (nextPerPage !== perPage) {
			setPerPage(nextPerPage);
			setPage(1);
		} else if (nextPage !== page) {
			setPage(nextPage);
		}
	};

	if (!contractId) return null;

	return (
		<SectionContainer>
			<SectionTitle>Edit History</SectionTitle>
			<AntdTable
				rowKey="key"
				loading={loading}
				columns={columns}
				dataSource={expandedRows}
				onChange={handleChange}
				pagination={{
					current: page,
					pageSize: perPage,
					total: expandedRows.length,
					showSizeChanger: true,
					position: ['bottomRight'],
				}}
			/>
		</SectionContainer>
	);
}

ContractEditHistoryTable.propTypes = {
	contractId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	contractType: PropTypes.oneOf(['vendor', 'client']).isRequired,
	status: PropTypes.string,
};

ContractEditHistoryTable.defaultProps = {
	contractId: null,
	status: 'approved',
};
